import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';

interface Holding {
  id: string;
  symbol: string;
  name: string;
  amount: number;
  avgBuyPrice: number;
  currentPrice: number;
  value: number;
  pnl: number;
  pnlPercentage: number;
  change24h: number;
  allocation: number;
  logo: string;
  transactions: Array<{
    date: string;
    type: 'buy' | 'sell';
    amount: number;
    price: number;
    value: number;
  }>;
}

interface HoldingsListProps {
  holdings: Holding[];
}

const HoldingsList: React.FC<HoldingsListProps> = ({ holdings }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'value' | 'pnl' | 'allocation'>('value');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const sortedHoldings = [...holdings].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
  });

  const toggleSort = (field: 'value' | 'pnl' | 'allocation') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl">
      <div className="p-6 border-b border-zinc-800">
        <h2 className="text-xl font-semibold text-white">Holdings</h2>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 text-sm font-medium text-zinc-400">Asset</th>
              <th className="text-right p-4 text-sm font-medium text-zinc-400">Amount</th>
              <th className="text-right p-4 text-sm font-medium text-zinc-400">Avg Buy Price</th>
              <th className="text-right p-4 text-sm font-medium text-zinc-400">Current Price</th>
              <th className="text-right p-4 text-sm font-medium text-zinc-400">24h</th>
              <th
                className="text-right p-4 text-sm font-medium text-zinc-400 cursor-pointer hover:text-white transition-colors"
                onClick={() => toggleSort('value')}
              >
                Value {sortBy === 'value' && (sortOrder === 'desc' ? '↓' : '↑')}
              </th>
              <th
                className="text-right p-4 text-sm font-medium text-zinc-400 cursor-pointer hover:text-white transition-colors"
                onClick={() => toggleSort('pnl')}
              >
                P&L {sortBy === 'pnl' && (sortOrder === 'desc' ? '↓' : '↑')}
              </th>
              <th
                className="text-right p-4 text-sm font-medium text-zinc-400 cursor-pointer hover:text-white transition-colors"
                onClick={() => toggleSort('allocation')}
              >
                Allocation {sortBy === 'allocation' && (sortOrder === 'desc' ? '↓' : '↑')}
              </th>
              <th className="text-center p-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings.map((holding) => (
              <React.Fragment key={holding.id}>
                <tr className="border-b border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={holding.logo}
                        alt={holding.symbol}
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <div className="font-medium text-white">{holding.symbol}</div>
                        <div className="text-sm text-zinc-400">{holding.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right p-4 text-white">{holding.amount.toLocaleString()}</td>
                  <td className="text-right p-4 text-zinc-300">
                    ${holding.avgBuyPrice.toLocaleString()}
                  </td>
                  <td className="text-right p-4 text-white">
                    ${holding.currentPrice.toLocaleString()}
                  </td>
                  <td className="text-right p-4">
                    <div className={`flex items-center justify-end gap-1 ${
                      holding.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {holding.change24h >= 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      {Math.abs(holding.change24h).toFixed(2)}%
                    </div>
                  </td>
                  <td className="text-right p-4 text-white font-medium">
                    ${holding.value.toLocaleString()}
                  </td>
                  <td className="text-right p-4">
                    <div className={holding.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                      <div className="font-medium">
                        {holding.pnl >= 0 ? '+' : ''}${holding.pnl.toLocaleString()}
                      </div>
                      <div className="text-sm">
                        ({holding.pnlPercentage >= 0 ? '+' : ''}{holding.pnlPercentage.toFixed(2)}%)
                      </div>
                    </div>
                  </td>
                  <td className="text-right p-4 text-zinc-300">
                    {holding.allocation.toFixed(1)}%
                  </td>
                  <td className="text-center p-4">
                    <button
                      onClick={() => setExpandedId(expandedId === holding.id ? null : holding.id)}
                      className="text-zinc-400 hover:text-white transition-colors"
                    >
                      {expandedId === holding.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
                {expandedId === holding.id && (
                  <tr>
                    <td colSpan={9} className="p-0">
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="bg-zinc-800/30 border-b border-zinc-800"
                      >
                        <div className="p-6">
                          <h4 className="text-sm font-medium text-zinc-400 mb-4">Transaction History</h4>
                          <div className="space-y-2">
                            {holding.transactions.map((tx, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-zinc-900/50 rounded-lg p-3"
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                                    tx.type === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {tx.type.toUpperCase()}
                                  </div>
                                  <div className="text-sm text-zinc-400">{tx.date}</div>
                                  <div className="text-sm text-white">
                                    {tx.amount.toLocaleString()} {holding.symbol}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-white">${tx.value.toLocaleString()}</div>
                                  <div className="text-xs text-zinc-400">@ ${tx.price.toLocaleString()}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedHoldings.map((holding) => (
          <div key={holding.id} className="border-b border-zinc-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <img
                  src={holding.logo}
                  alt={holding.symbol}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <div className="font-medium text-white text-lg">{holding.symbol}</div>
                  <div className="text-sm text-zinc-400">{holding.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-medium text-white">
                  ${holding.value.toLocaleString()}
                </div>
                <div className={`text-sm ${holding.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {holding.pnl >= 0 ? '+' : ''}${holding.pnl.toLocaleString()}
                  ({holding.pnlPercentage >= 0 ? '+' : ''}{holding.pnlPercentage.toFixed(2)}%)
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-400">Amount:</span>
                <span className="text-white ml-2">{holding.amount.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-zinc-400">24h:</span>
                <span className={`ml-2 ${holding.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {holding.change24h >= 0 ? '+' : ''}{holding.change24h.toFixed(2)}%
                </span>
              </div>
              <div>
                <span className="text-zinc-400">Avg Price:</span>
                <span className="text-white ml-2">${holding.avgBuyPrice.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-zinc-400">Current:</span>
                <span className="text-white ml-2">${holding.currentPrice.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HoldingsList;